import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import EPTierLock from '@/comp/common/EPTierLock';
import EPTierProtection from '@/comp/common/EPTierProtection';
import { ComponentTierName, interestedPackageEnum } from '@/constants/enums';
import { useValueByConnectivity } from '@/hooks/useValueByConnectivity';
import LockIcon from '@/comp/common/icons/LockIcon';
import FilledArrowIcon from '@/comp/common/icons/FilledArrowIcon';
import { DetailsWidgetSliderProps } from '@/comp/StationControlCenter/ControlCenterCompressorWidget/DetailsWidgetSlider/DetailsWidgetSlider.types';

import { Container } from './Slider.styles';
import { getAdjustedMax, getAdjustedMin, renderPoints } from './Slider.utils';

const Slider = observer(
  ({
    title,
    min,
    max,
    points,
    value,
    unit,
    connectivity,
    highlightSlider,
    addSideGaps = false,
    showTierLock = false,
    hasTarget = false,
  }: DetailsWidgetSliderProps) => {
    const { tcvalue } = useValueByConnectivity();
    const { t } = useTranslation();
    const adjustedMin = getAdjustedMin(min, max);
    const adjustedMax = getAdjustedMax(min, max);
    const targetPoint = points.find(point => point.isTarget);

    return (
      <Container
        $min={adjustedMin}
        $max={adjustedMax}
        $value={value}
        $highlightSlider={highlightSlider}
        $addSideGaps={addSideGaps}
        className="details-widget-slider"
        $unit={unit}
        $hasTarget={hasTarget && !!targetPoint?.value}
      >
        <div className="title-wrapper-tier">
          <div>{title && <span className="title">{title}</span>}</div>

          {showTierLock && (
            <EPTierProtection componentName={ComponentTierName.CompressorModalLockButton}>
              <div className="lock-wrapper">
                <EPTierLock
                  label={t('enableSystemControl')}
                  tooltipTextPart2={t('alwaysPerform')}
                  icon={<LockIcon />}
                  tooltipWidth={300}
                  emailRequestTypeOfInterestedPackage={interestedPackageEnum.SYSTEM_CONTROL}
                  emailRequestButtonClicked={'Lock Button'}
                  emailRequestWhichScreen={'Compressor Details'}
                />
              </div>
            </EPTierProtection>
          )}
        </div>

        <div className="slider-wrapper">
          {hasTarget && (
            <div className={cn('target-value', !targetPoint?.value && 'target-value-hidden')}>
              <span className="target-value-label">{t('Target')}:&nbsp;</span>
              <span className="target-value-value">{targetPoint?.value}</span>
              <span className="target-value-unit"> {unit}</span>
            </div>
          )}

          <div className="slider-container">
            <div className="value-container">
              <div className="value-arrow-block">
                <div className="value">
                  <span className="value-content"> {tcvalue(value, connectivity)} </span>
                  <span className="units-sign"> {value != null && !isNaN(value) && unit} </span>
                </div>
                <div className="triangle">
                  <FilledArrowIcon />
                </div>
              </div>
            </div>

            <div className="slider">
              <div className="filler" />
              <div className="grey-filler" />
            </div>

            <div className="points">{renderPoints(points, adjustedMin, adjustedMax, addSideGaps)}</div>
          </div>
        </div>
      </Container>
    );
  },
);

export default Slider;
